import { savePuzzleProgress } from '../../../utils/persistenceUtils';
import { ArithmeglyphsData, PuzzleNames, STATS_DIALOG_DELAY } from '../../../utils/types';
import { SpecialKeys } from '../shared/utils';
import { ArithmeglyphsStore, ArithmeglyphsStoreType } from './ArithmeglyphsStore';

export const onComplete = () => {
  const { time, revealed, puzzleId } = ArithmeglyphsStore.getRawState();

  setTimeout(() => {
    ArithmeglyphsStore.update(s => {
      s.statsDialogIsOpen = true;
    });
  }, STATS_DIALOG_DELAY);

  savePuzzleProgress(PuzzleNames.Arithmeglyphs, {
    puzzleId,
    revealed,
    solved: !revealed,
    time,
    completed: true,
    partialState: null,
  });
};

export const saveArithmeglyphs = () => {
  const { solved, revealed, completed, time, dataByLine, puzzleId }
    = ArithmeglyphsStore.getRawState();

  // just updating progress on pause or navigate away
  savePuzzleProgress(PuzzleNames.Arithmeglyphs, {
    puzzleId,
    revealed,
    solved,
    time,
    completed,
    partialState: completed ? null : {
      dataByLine: dataByLine.map(line => ({
        guesses: line.guesses,
        revealedCells: line.revealedCells,
      })),
      timeOfLastUpdate: new Date().toISOString(),
    },
  });
};

export const initArithmeglyphs = (puzzleData: ArithmeglyphsData) => {
  ArithmeglyphsStore.update(s => {
    s.puzzleId = puzzleData.puzzleId;
    s.dataByLine = [{
      guesses: Array(puzzleData.line1_numSeq.length).fill(null),
      opSeq: puzzleData.line1_opSeq,
      numSeq: puzzleData.line1_numSeq,
      revealedCells: Array(puzzleData.line1_numSeq.length).fill(false),
      mistakes: Array(puzzleData.line1_numSeq.length).fill(false),
    },
    {
      guesses: Array(puzzleData.line2_numSeq.length).fill(null),
      opSeq: puzzleData.line2_opSeq,
      numSeq: puzzleData.line2_numSeq,
      revealedCells: Array(puzzleData.line1_numSeq.length).fill(false),
      mistakes: Array(puzzleData.line1_numSeq.length).fill(false),
    }];
    s.thirdLineNumSeq = puzzleData.line3_numSeq;
  });
};

export enum ConfirmationTypes {
  CheckGlyph = 'CheckGlyph',
  CheckPuzzle = 'CheckPuzzle',
  RevealGlyph = 'RevealGlyph',
  RevealPuzzle = 'RevealPuzzle',
};

const isCompleted = (dataByLine: ArithmeglyphsStoreType['dataByLine']) => {
  const firstLine = dataByLine[0];

  let complete = true;
  firstLine.numSeq.split('').forEach((solutionDigit, i) => {
    if (firstLine.revealedCells[i]) return;
    if (firstLine.guesses[i] !== solutionDigit) {
      complete = false;
    }
  });
  return complete;
};

export const checkGlyph = () => {
  const { focusLocation: { lineNumber, index }, dataByLine } = ArithmeglyphsStore.getRawState();
  const guess = dataByLine[lineNumber].guesses[index];
  const solution = dataByLine[lineNumber].numSeq[index];
  if (guess === null) return;
  if (dataByLine[lineNumber].revealedCells[index]) return;

  ArithmeglyphsStore.update(s => {
    s.revealed = true;
    if (guess === solution) {
      s.dataByLine[lineNumber].revealedCells[index] = true;
      setTimeout(() => updateCursorPosition(SpecialKeys.Tab, false), 50);
    } else {
      s.dataByLine[lineNumber].mistakes[index] = true;
    }
  });
};

export const checkPuzzle = () => {
  const { dataByLine } = ArithmeglyphsStore.getRawState();

  ArithmeglyphsStore.update(s => {
    s.revealed = true;
    dataByLine.forEach((line, lineNumber) => {
      line.guesses.forEach((guess, index) => {
        const solution = line.numSeq[index];
        if (guess === null) return;
        if (s.dataByLine[lineNumber].revealedCells[index]) return;

        if (guess === solution) {
          s.dataByLine[lineNumber].revealedCells[index] = true;
        } else {
          s.dataByLine[lineNumber].mistakes[index] = true;
        }
      });
    });
  });
};

export const revealGlyph = () => {
  ArithmeglyphsStore.update(s => {
    const { lineNumber, index } = s.focusLocation;
    s.dataByLine[lineNumber].revealedCells[index] = true;
    s.dataByLine[lineNumber].mistakes[index] = false;
    s.revealed = true;
    if (isCompleted(s.dataByLine)) {
      s.completed = true;
      setTimeout(onComplete, 100);
    } else {
      // move cursor to next unrevealed cell
      // it's in a setTimeout cuz we're updating store from inside a store update
      setTimeout(() => updateCursorPosition(SpecialKeys.Tab, false), 50);
    }
  });
};

export const revealPuzzle = () => {
  ArithmeglyphsStore.update(s => {
    s.revealed = true;
    s.completed = true;
    s.dataByLine.forEach(line => {
      line.revealedCells.fill(true);
      line.mistakes.fill(false);
    });
  });
  setTimeout(onComplete, 100);
};

export const updateCursorPosition = (key: SpecialKeys | string, shift: boolean) => {
  const { focusLocation, dataByLine } = ArithmeglyphsStore.getRawState();
  const { lineNumber, index } = focusLocation;
  const line0Length = dataByLine[0].numSeq.length;
  const line1Length = dataByLine[1].numSeq.length;
  let isAtEndOfLine = index === dataByLine[lineNumber].numSeq.length - 1;
  const differenceInLineLengths = line1Length - line0Length;
  const upDownIndexOffset = Math.max(differenceInLineLengths - 1, 0);

  let newIndex = index;
  let newLineNumber = lineNumber

  if (key === SpecialKeys.Tab) {
    if (shift) {
      do {
        if (newIndex > 0) {
          newIndex--;
        } else if (newLineNumber === 1) {
          newLineNumber = 0;
          newIndex = line0Length - 1;
        } else {
          newLineNumber = 1;
          newIndex = line1Length - 1;
        }
      } while (dataByLine[newLineNumber].revealedCells[newIndex]);
    } else {
      do {
        if (!isAtEndOfLine) {
          newIndex++;
        } else if (newLineNumber === 0) {
          newLineNumber = 1;
          newIndex = 0;
        } else if (newLineNumber === 1) {
          newLineNumber = 0;
          newIndex = 0;
        }
        isAtEndOfLine = newIndex === dataByLine[newLineNumber].numSeq.length - 1;
      } while (dataByLine[newLineNumber].revealedCells[newIndex]);
    }
  }

  if (key === SpecialKeys.ArrowUp) {
    if (lineNumber === 0) {
      return;
    } else {
      newLineNumber = 0;
      newIndex = Math.max(index - upDownIndexOffset, 0);
      if (newIndex > line0Length - 1) newIndex = line0Length - 1;
    }
  }

  if (key === SpecialKeys.ArrowDown) {
    if (lineNumber === 1) {
      return;
    } else {
      newLineNumber = 1;
      newIndex = Math.min(index + upDownIndexOffset, line1Length - 1);
    }
  }

  if (key === SpecialKeys.ArrowRight) {
    if (!isAtEndOfLine) {
      newIndex++;
    }
  }

  if (key === SpecialKeys.ArrowLeft) {
    if (index !== 0) {
      newIndex--;
    }
  }

  ArithmeglyphsStore.update(s => {
    s.focusLocation = {
      lineNumber: newLineNumber,
      index: newIndex,
    };
  });
};

export const handleInput = (key: string, shift: boolean) => {
  const { focusLocation: { lineNumber, index }, completed } = ArithmeglyphsStore.getRawState();
  if (completed) return;
  if (/^[0-9]$/.test(key)) {
    if (key === '0' && shouldDisableZeroButton({ lineNumber, index })) {
      alert('Zeroes are not allowed on the first line or in circle operators.')
      return;
    };
    ArithmeglyphsStore.update(s => {
      // if this cell was revealed, just move cursor to next open cell
      if (s.dataByLine[lineNumber].revealedCells[index]) {
        setTimeout(() => updateCursorPosition(SpecialKeys.Tab, false), 50);
        return;
      }

      // clear out mistake marker (from Check Glyph/Puzzle) on change
      s.dataByLine[lineNumber].mistakes[index] = false;
      s.dataByLine[lineNumber].guesses[index] = key;

      if (isCompleted(s.dataByLine)) {
        s.completed = true;
        if (!s.revealed) s.solved = true;
        onComplete();
      } else {
        setTimeout(() => updateCursorPosition(SpecialKeys.Tab, false), 50);
      }
    });
  } else if (key === SpecialKeys.Delete) {
    ArithmeglyphsStore.update(s => {
      const currentGlyphIsBlank = !s.dataByLine[lineNumber].guesses[index];
      if (currentGlyphIsBlank && index > 0) {
        s.focusLocation.index = index - 1;
        s.dataByLine[lineNumber].guesses[index - 1] = null;
      } else {
        s.dataByLine[lineNumber].guesses[index] = null;
      }
    });
  } else if (key in SpecialKeys) {
    updateCursorPosition(key, shift);
  }
};

export const maybeConfirmAndProceed = (confirmationType: ConfirmationTypes) => {
  const { revealed, completed } = ArithmeglyphsStore.getRawState();
  if (completed) return;

  switch (confirmationType) {
    case ConfirmationTypes.CheckGlyph: {
      if (revealed) {
        checkGlyph();
      } else {
        ArithmeglyphsStore.update(s => {
          s.confirmationDialogs.checkGlyphIsOpen = true;
          s.isSoftPaused = true;
        });
      }
      break;
    }
    case ConfirmationTypes.CheckPuzzle: {
      if (revealed) {
        checkPuzzle();
      } else {
        ArithmeglyphsStore.update(s => {
          s.confirmationDialogs.checkPuzzleIsOpen = true;
          s.isSoftPaused = true;
        });
      }
      break;
    }
    case ConfirmationTypes.RevealGlyph: {
      if (revealed) {
        revealGlyph();
      } else {
        ArithmeglyphsStore.update(s => {
          s.confirmationDialogs.revealGlyphIsOpen = true;
          s.isSoftPaused = true;
        });
      }
      break;
    }
    case ConfirmationTypes.RevealPuzzle: {
      // always confirm this, even if it's already marked Revealed
      ArithmeglyphsStore.update(s => {
        s.confirmationDialogs.revealPuzzleIsOpen = true;
        s.isSoftPaused = true;
      });
      break;
    }
  }
};

// don't allow zeros on the first line or in circle operators
export const shouldDisableZeroButton = (focusLocation: {
  lineNumber: number,
  index: number,
}) => {
  const { lineNumber, index } = focusLocation;
  if (lineNumber === 0) return true;

  const opString = ArithmeglyphsStore.getRawState().dataByLine[lineNumber].opSeq;
  const opArray = opString.replace(/[^\w\s]/g, '').split(/\s+/).filter(word => word.length > 0);
  return opArray[index] === 'circle';
};